import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const paragraphStyle = {
  fontSize: {
    xs: "1rem",
    md: "1.15rem",
  },
  lineHeight: 1.8,
  color: "#374151",
};

const listStyle = {
  pl: 3,
  lineHeight: 2,
  color: "#374151",
};
const policySections = [
  {
    title: "1. Information We Collect",
    content: (
      <>
        <Typography sx={paragraphStyle}>
          When you use our application, we may collect the following categories
          of information:
        </Typography>

        <Box component="ul" sx={listStyle}>
          <li>
            <strong>Personal Details:</strong> Such as your name, phone number,
            email address, and any other information you voluntarily provide.
          </li>

          <li>
            <strong>Device Information:</strong> Including your device type,
            operating system, unique device identifiers, IP address, and
            application version.
          </li>

          <li>
            <strong>Location Information:</strong> With your permission, we may
            access your device's location to provide location-based features and
            improve our services.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "2. Purpose of Data Collection",
    content: (
      <>
        <Typography sx={paragraphStyle}>
          The information we collect may be used to:
        </Typography>

        <Box component="ul" sx={listStyle}>
          <li>Deliver and maintain the functionality of our application.</li>
          <li>Improve user experience and enhance app performance.</li>
          <li>Provide customer support and respond to your inquiries.</li>
          <li>Send important service-related notifications.</li>
          <li>Meet applicable legal and regulatory requirements.</li>
        </Box>
      </>
    ),
  },
  {
    title: "3. Disclosure of Information",
    content: (
      <>
        <Typography sx={paragraphStyle}>
          Your information may be shared only in the following circumstances:
        </Typography>

        <Box component="ul" sx={listStyle}>
          <li>
            With trusted partners or service providers who assist in operating
            and maintaining our services.
          </li>
          <li>
            When disclosure is required by law, court order, or government
            authority.
          </li>
          <li>
            To protect the rights, security, and integrity of our company,
            users, or the public where legally permitted.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "4. Protecting Your Information",
    content: (
      <Typography sx={paragraphStyle}>
        We implement appropriate technical and organizational security
        measures, including encryption, secure servers, and controlled access
        mechanisms, to help safeguard your personal information against
        unauthorized access, alteration, disclosure, or loss.
      </Typography>
    ),
  },
  {
    title: "5. Your Privacy Choices",
    content: (
      <>
        <Typography sx={paragraphStyle}>
          You have the right to:
        </Typography>

        <Box component="ul" sx={listStyle}>
          <li>Request access to the personal information we hold about you.</li>
          <li>Request correction of inaccurate or incomplete information.</li>
          <li>
            Request deletion of your personal data, subject to applicable legal
            obligations.
          </li>
        </Box>

        <Typography sx={paragraphStyle}>
          For any such requests, please contact us using the details provided
          below.
        </Typography>
      </>
    ),
  },
  {
    title: "6. Data Retention",
    content: (
      <Typography sx={paragraphStyle}>
        Personal information is retained only for the period necessary to
        provide our services, comply with legal obligations, resolve disputes,
        and enforce our agreements. Once no longer required, the information is
        securely deleted or anonymized.
      </Typography>
    ),
  },
  {
    title: "7. Children's Privacy",
    content: (
      <Typography sx={paragraphStyle}>
        Our application is intended for users who are 18 years of age or older.
        We do not knowingly collect personal information from individuals under
        the age of 18.
      </Typography>
    ),
  },
  {
    title: "8. Updates to This Privacy Policy",
    content: (
      <Typography sx={paragraphStyle}>
        We may revise this Privacy Policy from time to time. Any modifications
        will become effective once published within the application or through
        other appropriate communication channels.
      </Typography>
    ),
  },
  {
    title: "9. Contact Us",
    content: (
      <>
        <Typography sx={paragraphStyle}>
          If you have any questions, concerns, or requests regarding this
          Privacy Policy or your personal information, please contact:
        </Typography>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.4rem",
            mb: 2,
          }}
        >
          Precitrack Mapwala Pvt. Ltd.
        </Typography>

        <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
          📧 harish@mapwala.in
        </Typography>

        <Typography sx={{ fontSize: "1.1rem" }}>
          📞 +91 99114 43468
        </Typography>
      </>
    ),
  },
];

const PrivacyPolicy = () => {
  const [expanded, setExpanded] = useState(null);

  const handleToggle = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fff",
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 4,
            fontSize: {
              xs: "2rem",
              md: "2.5rem",
            },
          }}
        >
          Privacy Policy
        </Typography>

        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1.1rem",
            mb: 4,
          }}
        >
          Effective Date:
          <Box component="span" sx={{ fontWeight: 400, ml: 1 }}>
            21.08.2025
          </Box>
        </Typography>

        <Typography sx={paragraphStyle}>
          Precitrack Mapwala Pvt. Ltd. ("we", "our", or "us") respects your
          privacy and is committed to safeguarding the information you share
          while using our mobile application. This Privacy Policy explains what
          information we collect, why we collect it, how it is used, and the
          measures we take to protect it.
        </Typography>

        {policySections.map((section, index) => (
          <Box
            key={index}
            sx={{
              borderBottom: "1px solid #e5e7eb",
              py: 2,
            }}
          >
            <Box
              onClick={() => handleToggle(index)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: {
                    xs: "1.4rem",
                    md: "1.8rem",
                  },
                }}
              >
                {section.title}
              </Typography>

              <IconButton>
                {expanded === index ? (
                  <RemoveIcon fontSize="large" />
                ) : (
                  <AddIcon fontSize="large" />
                )}
              </IconButton>
            </Box>

            <Collapse in={expanded === index}>
              <Box sx={{ pt: 2 }}>{section.content}</Box>
            </Collapse>
          </Box>
        ))}
      </Container>
    </Box>
  );
};


export default PrivacyPolicy;